import { onBeforeUnmount, Ref, shallowRef, watch } from 'vue';

/** Rows in the first chunk (about a screenful) and bounds for later ones. */
const FIRST_CHUNK = 20;
const MIN_CHUNK = 10;
const MAX_CHUNK = 400;
/** Frames shorter than this double the next chunk; longer ones halve it. */
const FAST_FRAME_MS = 50;
const SLOW_FRAME_MS = 90;

/**
 * Splits `items` into chunks and adds one chunk per animation frame until all
 * of them are rendered.
 *
 * Mounting a few thousand table rows in one go blocks the main thread for
 * seconds on an ordinary laptop, which freezes the app bar menus. Rendering a
 * chunk per frame keeps every task short, and every row is in the DOM once the
 * last chunk renders, so the browser's find-in-page still searches the whole
 * list. Chunks never change once added, so a template can skip the rendered
 * ones with `v-memo="[chunk]"`. The chunk grows while frames stay fast and
 * shrinks when they get slow, so a fast machine finishes quickly and a slow
 * one stays responsive. A new `items` value (search, sort, reload) starts
 * again from the top.
 */
export default function useProgressiveList<T>(items: Ref<T[]>) {
  const chunks = shallowRef<T[][]>([]);
  let rendered = 0;
  let size = FIRST_CHUNK;
  let lastFrame: number | undefined;
  let frame: number | undefined;

  const stop = () => {
    if (frame !== undefined) {
      cancelAnimationFrame(frame);
      frame = undefined;
    }
  };

  const addChunk = () => {
    chunks.value = [
      ...chunks.value,
      items.value.slice(rendered, rendered + size),
    ];
    rendered += size;
    if (rendered < items.value.length) {
      frame = requestAnimationFrame(grow);
    }
  };

  function grow(now: number) {
    frame = undefined;
    if (lastFrame !== undefined) {
      const frameMs = now - lastFrame;
      if (frameMs < FAST_FRAME_MS) {
        size = Math.min(MAX_CHUNK, size * 2);
      } else if (frameMs > SLOW_FRAME_MS) {
        size = Math.max(MIN_CHUNK, Math.floor(size / 2));
      }
    }
    lastFrame = now;
    addChunk();
  }

  watch(
    items,
    () => {
      stop();
      chunks.value = [];
      rendered = 0;
      size = FIRST_CHUNK;
      lastFrame = undefined;
      if (items.value.length) {
        addChunk();
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(stop);

  return chunks;
}
